import { RtcLink, type RtcOptions } from '../webrtc/rtcLink';

/**
 * Manual signalling: offers/answers are exchanged as copy-paste codes, so no
 * server of any kind is required (works fully offline on a LAN). ICE gathering
 * is completed before the code is produced (no trickle).
 */
export interface ManualCode {
  v: 1;
  id: string;
  name?: string;
  sdp: RTCSessionDescriptionInit;
}

export function encodeCode(c: ManualCode): string {
  const json = JSON.stringify(c);
  return 'EA1.' + btoa(unescape(encodeURIComponent(json)));
}

export function decodeCode(code: string): ManualCode {
  const t = code.trim();
  if (!t.startsWith('EA1.')) throw new Error('Not a Four Crowns connection code.');
  const json = decodeURIComponent(escape(atob(t.slice(4))));
  const c = JSON.parse(json) as ManualCode;
  if (c.v !== 1 || !c.sdp?.sdp || !c.id) throw new Error('Malformed connection code.');
  return c;
}

/** Host side: create an invite for one joining player. */
export async function createInvite(localId: string, opts: RtcOptions): Promise<{ link: RtcLink; code: string }> {
  const link = new RtcLink('pending', opts, true);
  await link.createOffer();
  await link.waitIceComplete();
  return { link, code: encodeCode({ v: 1, id: localId, sdp: link.pc.localDescription!.toJSON() }) };
}

/** Joiner side: accept a host invite and produce the answer code. */
export async function acceptInvite(localId: string, name: string, invite: string, opts: RtcOptions): Promise<{ link: RtcLink; code: string; hostId: string }> {
  const c = decodeCode(invite);
  const link = new RtcLink(c.id, opts, false);
  await link.acceptOffer(c.sdp);
  await link.waitIceComplete();
  return { link, code: encodeCode({ v: 1, id: localId, name, sdp: link.pc.localDescription!.toJSON() }), hostId: c.id };
}

/** Host side: complete the connection with the joiner's answer code. */
export async function completeInvite(link: RtcLink, answer: string): Promise<string> {
  const c = decodeCode(answer);
  link.remoteId = c.id;
  await link.acceptAnswer(c.sdp);
  return c.id;
}
